import { Measures } from "../../../telemetry/Measures";
import { dashboard } from "../../../telemetry/TraceLogging";
import { getHostInstanceManagerApi } from "../../externalApi/InstanceManagerApi";
import { createAdaptiveCard, getHTMLElementFromCard, getVisualThemeString, logParseFailure, notifyWidgetProvider } from "./AdaptiveCardRenderingAndActions";
import { isFeatureEnabled } from "../../externalApi/VelocityApi";
const adaptiveCardTimeoutLength = 10000;
let savedCards = new Map();
export async function getAdaptiveCard(instance, height, width, actionProps) {
    // TODO: Optimize metrics after migration to WidgetService https://microsoft.visualstudio.com/OS/_workitems/edit/40012338
    let card = undefined;
    let errorType = "none";
    let contentType = "undefined";
    if (savedCards.has(instance.widgetId)) {
        contentType = "cachedContent";
    }
    else if (!actionProps) {
        // No actionProps means this is a general refresh due to size change etc... this is render only, no fetch for 3p widgets.
        // If there is no cached content return with nothing.
        // This is to handle refresh scenarios on init where no action occured and the widget has not sent data yet.
        // If there is no cached content for 3p widgets we don't want to explicitly fetch it until it notifies that it is updated.
        return [undefined, contentType, "none"];
    }
    let webWidgetUrl;
    try {
        // Get AdaptiveCard if there is not a saved card already or if there was a Submit or Execute action
        // When we call getAdaptiveCard for platform widgets this is always a refresh request
        if (instance.widgetRegistration.entryPoint.type === "PLATFORM" ||
            contentType !== "cachedContent" ||
            (actionProps && actionProps.length > 0)) {
            Measures.logEvent(dashboard, "AdaptiveCardGetStarted", 16777216 /* productAndServicePerformance */, {
                appId: instance.widgetRegistration.appId,
                providerId: instance.widgetRegistration.providerId,
                widgetDefinitionId: instance.widgetRegistration.id,
                widgetInstanceId: instance.widgetId,
            });
            let cardFromProvider = undefined;
            // In the platform case, handling the action is not connected to the data fetch.
            // The props would have been passed in the action handler.
            cardFromProvider = await Promise.race([
                getCardFromPlatform(instance),
                new Promise((_r, reject) => setTimeout(() => reject("timeoutPromise"), adaptiveCardTimeoutLength)),
            ]);
            Measures.logEvent(dashboard, "AdaptiveCardGetFinished", 16777216 /* productAndServicePerformance */, {
                appId: instance.widgetRegistration.appId,
                isCardFromProviderEmpty: cardFromProvider === undefined ? true : false,
                isPlatform: instance.widgetRegistration.entryPoint.type === "PLATFORM" ? true : false,
                providerId: instance.widgetRegistration.providerId,
                widgetDefinitionId: instance.widgetRegistration.id,
                widgetInstanceId: instance.widgetId,
            });
            if (cardFromProvider !== undefined) {
                if (isFeatureEnabled("Feature_WebWidgetPrototype") && cardFromProvider && cardFromProvider.data) {
                    let dataObject = JSON.parse(cardFromProvider.data);
                    if (dataObject && dataObject.hasOwnProperty("WebWidgetUrl")) {
                        webWidgetUrl = dataObject["WebWidgetUrl"];
                    }
                }
                // If the tempate is still empty, leave card = undefined with errorType = "none"
                // This will result in the fetch being ignored
                let returnedCard = undefined;
                let returnedError = "none";
                if (cardFromProvider.template && cardFromProvider.template.length !== 0 && cardFromProvider.template !== "{}") {
                    // Try to create Adaptive Card before replacing it with a valid savedCard
                    [returnedCard, returnedError] = createAdaptiveCard(instance, cardFromProvider);
                    errorType = returnedError;
                    if (returnedCard !== undefined) {
                        card = returnedCard;
                        contentType = "newContent";
                        savedCards.set(instance.widgetId, cardFromProvider);
                    }
                    else if (returnedCard === undefined && contentType === "cachedContent") {
                        errorType = "cardCreationError";
                    }
                }
            }
            else {
                notifyWidgetProvider(instance, { "errorKind": "cardCreationFailed", "message": "Failed to receive app payload" });
                if (contentType !== "cachedContent" && instance.widgetRegistration.entryPoint.type === "PLATFORM") {
                    errorType = "cardFromPlatformUndefined";
                }
                else {
                    // Widget will continue to show what's already there
                    errorType = "cardUndefined";
                }
            }
        }
        else if (actionProps && actionProps.length === 0) {
            // There was a Submit or Execute action but we do not get a new card
            notifyWidgetProvider(instance, { SubmitExecuteAction: "No Props" });
            updateHostContext(instance, "isSettingsPayload", "false");
        }
    }
    catch (e) {
        logParseFailure(instance, e.Message ? e.Message : e, "unknownException");
        notifyWidgetProvider(instance, { "errorKind": "cardCreationFailed", "message": e.Message ? e.Message : e });
        return [undefined, contentType, "exceptionThrown"];
    }
    // No old card for this widget instance, must show error card
    if (card === undefined && contentType !== "cachedContent" && errorType !== "none") {
        notifyWidgetProvider(instance, { "errorKind": "cardCreationFailed", "message": "Failed to create Adaptive Card" });
        return [undefined, contentType, errorType];
        // Has old card to fallback and continue showing old card
    }
    else if (contentType === "cachedContent" && errorType !== "none") {
        return [undefined, contentType, errorType];
    }
    else if (card === undefined) {
        return [undefined, contentType, errorType];
    }
    let cardDiv = undefined;
    // New Adaptive Card to show user
    if (card !== undefined && errorType === "none") {
        // If this card is a web reference, create an IFrame, navigate to the url, and skip to the end
        if (isFeatureEnabled("Feature_WebWidgetPrototype") && webWidgetUrl) {
            cardDiv = document.createElement("div");
            let iframe = document.createElement("iframe");
            iframe.src = webWidgetUrl;
            iframe.width = width;
            iframe.height = height;
            iframe.style.border = "none";
            iframe.style.paddingTop = "36px";
            iframe.name = instance.widgetDefinitionId;
            cardDiv.appendChild(iframe);
        }
        else {
            cardDiv = await getHTMLElementFromCard(card, instance, height, width);
        }
    }
    if (cardDiv === undefined) {
        return [undefined, "undefined", "cardDivUndefined"];
    }
    return [cardDiv, contentType, "none"];
}
export async function getCardFromPlatform(widgetInstance) {
    Measures.logEvent(dashboard, "GetAdaptiveCardFromPlatformStarted", 16777216 /* productAndServicePerformance */, {
        appId: widgetInstance.widgetRegistration.appId,
        providerId: widgetInstance.widgetRegistration.providerId,
        widgetDefinitionId: widgetInstance.widgetDefinitionId,
        widgetInstanceId: widgetInstance.widgetId,
    });
    let hostInstanceManagerApi = await getHostInstanceManagerApi();
    if (!hostInstanceManagerApi) {
        Measures.logEvent(dashboard, "GetAdaptiveCardFromPlatformFailed", 16777216 /* productAndServicePerformance */, {
            appId: widgetInstance.widgetRegistration.appId,
            hresult: 0x80004001,
            providerId: widgetInstance.widgetRegistration.providerId,
            reason: "MissingProviderAPI",
            widgetDefinitionId: widgetInstance.widgetDefinitionId,
            widgetInstanceId: widgetInstance.widgetId,
        });
        return undefined;
    }
    let theme = await getVisualThemeString();
    return new Promise((resolve) => {
        hostInstanceManagerApi === null || hostInstanceManagerApi === void 0 ? void 0 : hostInstanceManagerApi.getWidgetDataAsync(widgetInstance.widgetId, // In the widget platform case, widgetId is the unique instance ID guid, not a type name
        (hresult, data, template) => {
            var _a;
            if (hresult !== 0) {
                Measures.logEvent(dashboard, "GetAdaptiveCardFromPlatformFailed", 16777216 /* productAndServicePerformance */, {
                    appId: widgetInstance.widgetRegistration.appId,
                    hresult: hresult,
                    providerId: widgetInstance.widgetRegistration.providerId,
                    reason: "getWidgetDataAsync failed with code " + hresult,
                    theme: theme,
                    widgetDefinitionId: widgetInstance.widgetDefinitionId,
                    widgetInstanceId: widgetInstance.widgetId,
                });
                resolve(undefined);
            }
            const card = { data: data, hostContext: undefined, template: template };
            if (!card.data || card.data === "") {
                card.data = "{}";
            }
            // Add initial properties to host context for size, settings and theme
            let hostParams = {};
            hostParams.hostTheme = theme;
            hostParams.isSettingsPayload = "false";
            hostParams.widgetSize = widgetInstance.getWidgetProperty("size");
            card.hostContext = hostParams;
            Measures.logEvent(dashboard, "GetAdaptiveCardFromPlatformSucceeded", 16777216 /* productAndServicePerformance */, {
                appId: widgetInstance.widgetRegistration.appId,
                isDataEmpty: card.data === "{}" || card.data.length === 0,
                isTemplateEmpty: card.template === "{}" || ((_a = card.template) === null || _a === void 0 ? void 0 : _a.length) === 0,
                providerId: widgetInstance.widgetRegistration.providerId,
                theme: theme,
                widgetDefinitionId: widgetInstance.widgetDefinitionId,
                widgetInstanceId: widgetInstance.widgetId,
            });
            resolve(card);
        });
    });
}
export async function updateHostContext(widgetInstance, propertyName, newValue) {
    const isTombstoned = widgetInstance.widgetRegistration.isTombstoned;
    if (isTombstoned || widgetInstance.isOutlookWidget()) {
        savedCards.delete(widgetInstance.widgetId);
        return true;
    }
    let savedCard = savedCards.get(widgetInstance.widgetId);
    let shouldReRender = false;
    // Update the host context for size, settings or theme if necessary
    if (propertyName) {
        if (savedCard) {
            if (propertyName === "hostTheme") {
                newValue = await getVisualThemeString();
            }
            if (savedCard.hostContext.hasOwnProperty(propertyName) && savedCard.hostContext[propertyName] !== newValue) {
                savedCard.hostContext[propertyName] = newValue;
                // Host theme will re-render onPlatformWidgetRegistrationUpdated due to content refresh
                if (propertyName !== "hostTheme") {
                    shouldReRender = true;
                }
            }
            savedCards.set(widgetInstance.widgetId, savedCard);
        }
    }
    return shouldReRender;
}
